import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Play, Upload } from 'lucide-react';
import { apiFetch, getApiBase } from './apiBase';
import { maskUsDateInput, parseEffectiveDateInput } from './effectiveDateInput';
import CatalogReportTable from './CatalogReportTable';

function DateField({ label, value, onChange }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-slate-300">{label}</span>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="MM/DD/YYYY"
        className="input-field w-36 font-mono"
        value={value}
        onChange={(e) => onChange(maskUsDateInput(e.target.value))}
      />
    </label>
  );
}

function paramLabel(name) {
  const n = String(name || '');
  if (/effective/i.test(n)) return 'As-of date';
  if (/start|from|begin/i.test(n)) return 'Start date';
  if (/end|to|thru/i.test(n)) return 'End date';
  return n.replace(/^P_/, '').replace(/_/g, ' ');
}

function AdpCatalogReportCard({ summary, connectionRefresh }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [dates, setDates] = useState({});
  const [extraParams, setExtraParams] = useState({});
  const [running, setRunning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadDetail = useCallback(async () => {
    setLoadingDetail(true);
    setError('');
    try {
      const data = await apiFetch(`/api/reports/adp-catalog/${encodeURIComponent(summary.id)}`);
      setDetail(data);
      if (data?.rowCount > 0) setRefreshKey((k) => k + 1);
    } catch (e) {
      setError(e.message || 'Failed to load report');
    } finally {
      setLoadingDetail(false);
    }
  }, [summary.id]);

  useEffect(() => {
    if (open) loadDetail();
  }, [open, loadDetail, connectionRefresh]);

  const dateParams = detail?.dateParams || summary.dateParams || ['P_EFFECTIVE_DATE'];
  const liveAvailable = !!(detail?.liveRunAvailable ?? summary.liveRunAvailable);

  const nonDateBipParams = useMemo(
    () =>
      (detail?.parameters || []).filter((p) => {
        const blob = `${p.name} ${p.label} ${p.dataType}`.toLowerCase();
        return !/date/.test(blob);
      }),
    [detail]
  );

  const runReport = async () => {
    setError('');
    setResult(null);
    const parameters = { ...extraParams };
    for (const name of dateParams) {
      const raw = dates[name] || dates.P_EFFECTIVE_DATE || '';
      if (!raw.trim()) continue;
      const parsed = parseEffectiveDateInput(raw);
      if (!parsed.ok) {
        setError(`${paramLabel(name)}: ${parsed.error}`);
        return;
      }
      parameters[name] = parsed.iso;
    }
    setRunning(true);
    try {
      const data = await apiFetch(`/api/reports/adp-catalog/${encodeURIComponent(summary.id)}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parameters, saveToDocs: true }),
      });
      if (!data.ok) throw new Error(data.error || 'Report failed');
      setResult(data);
      setRefreshKey((k) => k + 1);
      await loadDetail();
    } catch (e) {
      setError(e.message || 'Report failed');
    } finally {
      setRunning(false);
    }
  };

  const uploadReport = async (file) => {
    if (!file) return;
    setError('');
    setResult(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(
        `${getApiBase()}/api/reports/adp-catalog/${encodeURIComponent(summary.id)}/upload`,
        { method: 'POST', credentials: 'include', body: fd }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setResult(data);
      setRefreshKey((k) => k + 1);
      await loadDetail();
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const uniqueDateParams = [...new Set(dateParams)];

  return (
    <div className="rounded-lg border border-slate-700/70 bg-slate-900/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 px-4 py-3 text-left"
      >
        {open ? (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
        ) : (
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-slate-100">{summary.title}</span>
          <span className="mt-0.5 block text-xs text-slate-500">{summary.description}</span>
          {summary.feeds?.length > 0 && (
            <span className="mt-1 block text-[11px] text-slate-600">
              Feeds {summary.feeds.join(', ')}
            </span>
          )}
        </span>
        <span className="shrink-0 text-xs text-slate-500">
          {summary.rowCount
            ? `${summary.rowCount.toLocaleString()} rows saved`
            : summary.inOracle === false
              ? 'Not listed in Oracle'
              : 'Not run yet'}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-slate-800 px-4 pb-4 pt-3">
          {loadingDetail && !detail && (
            <p className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading report definition…
            </p>
          )}

          <div className="flex flex-wrap items-end gap-3">
            {uniqueDateParams.map((name) => (
              <DateField
                key={name}
                label={paramLabel(name)}
                value={dates[name] || ''}
                onChange={(v) => setDates((d) => ({ ...d, [name]: v }))}
              />
            ))}
            {nonDateBipParams.map((p) => (
              <label key={p.name} className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-300">{p.label || p.name}</span>
                <input
                  className="input-field w-44 font-mono text-xs"
                  value={extraParams[p.name] ?? p.defaultValue ?? ''}
                  onChange={(e) => setExtraParams((prev) => ({ ...prev, [p.name]: e.target.value }))}
                  placeholder={p.name}
                />
              </label>
            ))}
            <button
              type="button"
              onClick={runReport}
              disabled={running || uploading || !liveAvailable}
              className="btn-primary"
              title={
                liveAvailable
                  ? `Run ${summary.title} from Oracle`
                  : 'This report is not available on the active connection'
              }
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {running ? 'Running (this can take several minutes)…' : 'Run report'}
            </button>
            <label className="btn-secondary inline-flex cursor-pointer">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload Excel
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                disabled={uploading || running}
                onChange={(e) => {
                  uploadReport(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
          </div>

          {detail?.catalogPath && (
            <p className="sr-only">{detail.catalogPath}</p>
          )}

          {error && <p className="alert-error">{error}</p>}
          {result?.ok && (
            <p className="alert-success text-sm">
              {result.rowCount?.toLocaleString()} rows
              {result.columnCount ? ` · ${result.columnCount} columns` : ''}
              {result.savedPath ? ' · saved' : ''}
            </p>
          )}

          <CatalogReportTable
            title={`${summary.title} results`}
            rowsUrl={`/api/reports/adp-catalog/${encodeURIComponent(summary.id)}/rows`}
            exportUrl={`/api/reports/adp-catalog/${encodeURIComponent(summary.id)}/export`}
            refreshKey={refreshKey}
          />
        </div>
      )}
    </div>
  );
}

export default function AdpCatalogReports({ connectionRefresh }) {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    apiFetch('/api/reports/adp-catalog')
      .then(setPayload)
      .catch((e) => {
        setError(e.message);
        setPayload(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load, connectionRefresh]);

  const reports = payload?.reports || [];

  return (
    <div className="card-panel p-5">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-100">Other Oracle payroll reports</h3>
          <p className="mt-1 text-xs text-slate-500">
            Additional extracts published on this Oracle tenant. Employee Master and Element
            Entries stay in the cards above. Run or upload, then use Apply filters on the results.
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading} className="btn-secondary text-xs">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
        </button>
      </div>

      {error && <p className="alert-error mb-3">{error}</p>}
      {payload?.listError && (
        <p className="alert-warning mb-3 text-xs">
          Could not list reports on this Oracle tenant. Showing the expected reports anyway.
        </p>
      )}

      {loading && !reports.length ? (
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading reports…
        </p>
      ) : (
        <div className="space-y-2">
          {reports.map((r) => (
            <AdpCatalogReportCard key={r.id} summary={r} connectionRefresh={connectionRefresh} />
          ))}
        </div>
      )}
    </div>
  );
}
